import { z } from 'zod/v3';
import { OrganizationRoleSchema, PlanSchema, ProductSchema } from '../generated/enums';
import { WorkspaceDTOSchema } from './workspace.dto';
// Base organization schema
export const OrganizationDTOSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string().nullable(),
  logo: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  metadata: z.string().nullable(),
  plan: PlanSchema.optional(),
  product: ProductSchema.optional(),
  // Partner management fields (RFC-091)
  isPartner: z.boolean().optional(),
  partnerTier: z.string().nullable().optional(),
  maxClients: z.number().nullable().optional(),
  parentPartnerId: z.string().nullable().optional(),
}).strict();

export const OrganizationWithWorkspacesDTOSchema = OrganizationDTOSchema.extend({
  workspaces: z.array(WorkspaceDTOSchema),
});

// Create organization
export const CreateOrganizationDTOSchema = z.object({
  name: z.string().min(2),
  slug: z.string().min(2),
  teamSize: z.enum(['1', '2-10', '11-50', '51-200', '200+']).optional(),
}).strict();

// Update organization
export const UpdateOrganizationDTOSchema = z.object({
  data: z.object({
    name: z.string().min(1).optional(),
    slug: z.string().min(2).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase alphanumeric with hyphens').optional(),
    teamSize: z.string().min(1).optional(),
    onboardingStep: z.string().optional(),
  }).strict(),
}).strict();

// Get organization by ID
export const GetOrganizationByIdDTOSchema = z.object({
  id: z.string(),
}).strict();

// Delete organization
export const DeleteOrganizationDTOSchema = z.object({
  id: z.string(),
}).strict();

// Add member to organization
export const AddOrganizationMemberDTOSchema = z.object({
  organizationId: z.string(),
  userId: z.string(),
  role: OrganizationRoleSchema.default('member'),
}).strict();

// Update member role
export const UpdateOrganizationMemberRoleDTOSchema = z.object({
  organizationId: z.string(),
  userId: z.string(),
  role: OrganizationRoleSchema,
}).strict();

// Remove member from organization
export const RemoveOrganizationMemberDTOSchema = z.object({
  organizationId: z.string(),
  userId: z.string(),
}).strict();

// Invite user to organization
export const InviteOrganizationUserDTOSchema = z.object({
  organizationId: z.string(),
  email: z.string().email(),
  role: OrganizationRoleSchema.default('member'),
  message: z.string().max(500).optional(),
}).strict();

// Cancel organization invitation
export const CancelOrganizationInvitationDTOSchema = z.object({
  invitationId: z.string(),
  organizationId: z.string(),
});

export const OrganizationWithRoleDTOSchema = OrganizationDTOSchema.extend({
  userRole: z.string(),
});

// Export types
export type OrganizationDTO = z.infer<typeof OrganizationDTOSchema>;
export type OrganizationWithRoleDTO = z.infer<typeof OrganizationWithRoleDTOSchema>;
export type CreateOrganizationDTO = z.infer<typeof CreateOrganizationDTOSchema>;
export type UpdateOrganizationDTO = z.infer<typeof UpdateOrganizationDTOSchema>;
export type GetOrganizationByIdDTO = z.infer<typeof GetOrganizationByIdDTOSchema>;
export type DeleteOrganizationDTO = z.infer<typeof DeleteOrganizationDTOSchema>;
export type AddOrganizationMemberDTO = z.infer<typeof AddOrganizationMemberDTOSchema>;
export type UpdateOrganizationMemberRoleDTO = z.infer<typeof UpdateOrganizationMemberRoleDTOSchema>;
export type RemoveOrganizationMemberDTO = z.infer<typeof RemoveOrganizationMemberDTOSchema>;
export type InviteOrganizationUserDTO = z.infer<typeof InviteOrganizationUserDTOSchema>;
export type OrganizationWithWorkspacesDTO = z.infer<typeof OrganizationWithWorkspacesDTOSchema>;
